import { API_BASE_URL } from './api';

export type ContractStatus = 'DRAFT' | 'PENDING_SIGNATURE' | 'SIGNED_BY_BUYER' | 'SIGNED_BY_SUPPLIER' | 'SIGNED_BY_BOTH' | 'SIGNED' | 'CANCELLED' | 'EXPIRED' | 'APPROVED' | 'PENDING_MODERATION' | 'NEEDS_REVISION';

export interface Contract {
    id: number;
    contractNumber: string;
    status: ContractStatus;
    createdAt: string;
    signedAt?: string;
    signatureData?: string;
    companyBin: string;
    companyName: string;
    generatedPdfPath?: string;
    cmsZipFilePath?: string;
    company?: {
        id: number;
        bin: string;
        name: string;
    };
    signatory?: {
        id: number;
        phoneNumber: string;
    };
}

export interface ContractStats {
    total: number;
    draft: number;
    pendingSignature: number;
    signed: number;
    cancelled: number;
    expired: number;
}

export interface ContractGenerationRequest {
    contractNumber: string;
    companyData: {
        bin: string;
        name: string;
        legalAddress: string;
        actualAddress: string;
        contactPhone: string;
        contactEmail: string;
        directorName: string;
        bank?: string;
        kbe?: string;
        bik?: string;
        accountNumber?: string;
    };
}

export interface ContractGenerationResponse {
    success: boolean;
    message?: string;
    data?: Contract;
    contractId?: number;
    pdfUrl?: string;
}

export interface ContractSigningRequest {
    signatureData: string;
    certificateData: string;
    signerInfo: any;
}

export interface ContractSignWithStampRequest {
    signatureData: string;
    certificateData: string;
    signerInfo: string;
    signerName?: string;
    organizationName?: string;
    signDate?: string;
    stampX?: number;
    stampY?: number;
    stampWidth?: number;
    stampHeight?: number;
    stampPage?: number;
}

export interface ContractSigningResponse {
    success: boolean;
    message?: string;
    signedContractUrl?: string;
}

class ContractService {
    private getAuthHeaders(): HeadersInit {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    }

    /**
     * Автоматически генерирует договор для текущего пользователя
     */
    async generateContract(): Promise<ContractGenerationResponse> {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/auto-generate`, {
                method: 'POST',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ошибка генерации договора');
            }

            return await response.json();
        } catch (error: any) {
            console.error('Ошибка генерации договора:', error);
            throw error;
        }
    }

    /**
     * Скачивает сгенерированный договор в PDF формате
     */
    async downloadContract(contractId: number): Promise<Blob> {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/${contractId}/download`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken') || localStorage.getItem('authToken')}`
                }
            });

            if (!response.ok) {
                throw new Error('Ошибка скачивания договора');
            }

            return await response.blob();
        } catch (error: any) {
            console.error('Ошибка скачивания договора:', error);
            throw error;
        }
    }

    /**
     * Подписывает договор электронной подписью
     */
    async signContract(contractId: number, signatureData: ContractSigningRequest): Promise<ContractSigningResponse> {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/${contractId}/sign`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(signatureData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ошибка подписания договора');
            }

            return await response.json();
        } catch (error: any) {
            console.error('Ошибка подписания договора:', error);
            throw error;
        }
    }

    /**
     * Подписывает договор с визуальным штампом
     */
    async signContractWithStamp(contractId: number, signatureData: ContractSignWithStampRequest): Promise<ContractSigningResponse> {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/${contractId}/sign-with-stamp`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(signatureData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ошибка подписания договора с штампом');
            }

            return await response.json();
        } catch (error: any) {
            console.error('Ошибка подписания договора с штампом:', error);
            throw error;
        }
    }

    /**
     * Подписывает договор штампом покупателя (правая колонка)
     */
    async signContractCustomer(contractId: number, signatureData: ContractSignWithStampRequest): Promise<ContractSigningResponse> {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/${contractId}/sign-customer`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(signatureData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ошибка подписания договора штампом покупателя');
            }

            return await response.json();
        } catch (error: any) {
            console.error('Ошибка подписания договора штампом покупателя:', error);
            throw error;
        }
    }

    /**
     * Подписывает договор штампом поставщика (левая колонка)
     */
    async signContractSupplier(contractId: number, signatureData: ContractSignWithStampRequest): Promise<ContractSigningResponse> {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/${contractId}/sign-supplier`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(signatureData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ошибка подписания договора штампом поставщика');
            }

            return await response.json();
        } catch (error: any) {
            console.error('Ошибка подписания договора штампом поставщика:', error);
            throw error;
        }
    }

    /**
     * Получает информацию о договоре
     */
    async getContract(contractId: number): Promise<any> {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/${contractId}`, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error('Ошибка получения договора');
            }

            return await response.json();
        } catch (error: any) {
            console.error('Ошибка получения договора:', error);
            throw error;
        }
    }

    /**
     * Получает список договоров пользователя
     */
    async getUserContracts(): Promise<any[]> {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/my`, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error('Ошибка получения списка договоров');
            }

            const data = await response.json();
            return data.data || [];
        } catch (error: any) {
            console.error('Ошибка получения списка договоров:', error);
            throw error;
        }
    }

    /**
     * Создает новый договор
     */
    async createContract(contractData: Partial<ContractGenerationRequest>): Promise<any> {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(contractData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ошибка создания договора');
            }

            return await response.json();
        } catch (error: any) {
            console.error('Ошибка создания договора:', error);
            throw error;
        }
    }

    /**
     * Отменяет договор
     */
    async cancelContract(contractId: number): Promise<any> {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/${contractId}/cancel`, {
                method: 'POST',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ошибка отмены договора');
            }

            return await response.json();
        } catch (error: any) {
            console.error('Ошибка отмены договора:', error);
            throw error;
        }
    }

    /**
     * Получает договоры компании по БИН
     */
    async getCompanyContracts(companyBin: string): Promise<{ success: boolean; data?: Contract[]; message?: string }> {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/company/${companyBin}`, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ошибка получения договоров компании');
            }

            return await response.json();
        } catch (error: any) {
            console.error('Ошибка получения договоров компании:', error);
            throw error;
        }
    }

    /**
     * Получает статистику договоров компании
     */
    async getContractStats(companyBin: string): Promise<{ success: boolean; data?: ContractStats; message?: string }> {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/stats/${companyBin}`, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ошибка получения статистики договоров');
            }

            return await response.json();
        } catch (error: any) {
            console.error('Ошибка получения статистики договоров:', error);
            throw error;
        }
    }

    /**
     * Генерирует PDF договора
     */
    async generateContractPdf(contractId: number): Promise<{ success: boolean; data?: any; message?: string }> {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/${contractId}/generate-pdf`, {
                method: 'POST',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ошибка генерации PDF');
            }

            return await response.json();
        } catch (error: any) {
            console.error('Ошибка генерации PDF:', error);
            throw error;
        }
    }

    /**
     * Скачивает договор по ID и номеру
     */
    async downloadContractById(contractId: number, contractNumber: string): Promise<void> {
        try {
            const blob = await this.downloadContract(contractId);

            // Создаем ссылку для скачивания
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${contractNumber}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            console.error('Ошибка скачивания договора:', error);
            throw error;
        }
    }

    /**
     * Скачивает CMS zip архив с подписями через backend (обходит CORS)
     */
    async downloadCmsZipFile(contractId: number, contractNumber: string): Promise<void> {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/${contractId}/download-cms-zip`, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error('Ошибка скачивания CMS архива');
            }

            const blob = await response.blob();

            // Создаем ссылку для скачивания
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `CMS_подписи_${contractNumber}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            console.error('Ошибка скачивания CMS архива:', error);
            throw error;
        }
    }

    // Вспомогательные методы для работы со статусами

    /**
     * Получает отображаемое название статуса
     */
    getStatusDisplayName(status: ContractStatus): string {
        const statusNames: Record<ContractStatus, string> = {
            'DRAFT': 'Черновик',
            'PENDING_SIGNATURE': 'Ожидает подписания',
            'SIGNED_BY_BUYER': 'Подписан покупателем',
            'SIGNED_BY_SUPPLIER': 'Подписан поставщиком',
            'SIGNED_BY_BOTH': 'Подписан обеими сторонами',
            'SIGNED': 'Подписан',
            'CANCELLED': 'Отменен',
            'EXPIRED': 'Истек срок действия',
            'APPROVED': 'Одобрен',
            'PENDING_MODERATION': 'На модерации',
            'NEEDS_REVISION': 'Требует доработки'
        };
        return statusNames[status] || status;
    }

    /**
     * Получает CSS класс для статуса
     */
    getStatusColor(status: ContractStatus): string {
        const statusColors: Record<ContractStatus, string> = {
            'DRAFT': 'bg-gray-100 text-gray-800',
            'PENDING_SIGNATURE': 'bg-yellow-100 text-yellow-800',
            'SIGNED_BY_BUYER': 'bg-blue-100 text-blue-800',
            'SIGNED_BY_SUPPLIER': 'bg-indigo-100 text-indigo-800',
            'SIGNED_BY_BOTH': 'bg-green-100 text-green-800',
            'SIGNED': 'bg-green-100 text-green-800',
            'CANCELLED': 'bg-red-100 text-red-800',
            'EXPIRED': 'bg-orange-100 text-orange-800',
            'APPROVED': 'bg-green-100 text-green-800',
            'PENDING_MODERATION': 'bg-yellow-100 text-yellow-800',
            'NEEDS_REVISION': 'bg-orange-100 text-orange-800'
        };
        return statusColors[status] || 'bg-gray-100 text-gray-800';
    }

    /**
     * Проверяет можно ли скачать договор
     */
    canDownloadContract(contract: Contract): boolean {
        return contract.status === 'PENDING_SIGNATURE' || contract.status === 'SIGNED';
    }

    /**
     * Проверяет можно ли подписать договор
     */
    canSignContract(status: ContractStatus): boolean {
        return status === 'PENDING_SIGNATURE';
    }

    /**
     * Проверяет можно ли отменить договор
     */
    canCancelContract(status: ContractStatus): boolean {
        return status === 'DRAFT' || status === 'PENDING_SIGNATURE';
    }
}

export const contractService = new ContractService();